// src/components/production/MonitoramentoSubTab.js
import React, { useMemo, useState, useEffect } from "react";
import { 
  Truck, Calendar, Copy, Check, Eye, RefreshCw, ExternalLink, 
  CheckCircle, AlertTriangle, AlertCircle, XCircle, Info, CalendarRange
} from "lucide-react";
import { formatMoney } from "../../utils/formatters";

// Helper local para agrupar pedidos exatamente como a logica de producao faz
function groupOrdersLocal(rawData) {
  const groups = {};

  rawData.forEach((item) => {
    const orderNum = item.order?.number || "AVULSO";

    if (!groups[orderNum]) {
      let cName = "Cliente Balcão";
      if (orderNum !== "AVULSO" && item.order?.customer?.name) {
        cName = item.order.customer.name;
      }

      groups[orderNum] = {
        orderNumber: orderNum,
        customerName: cName,
        date: item.created ? new Date(item.created) : new Date(),
        totalValue: 0,
        shippingMethod: item.shipping?.tipoenvio || item.shipping?.method || "Retirada",
        items: [],
        referenceIds: [],
      };
    }

    groups[orderNum].items.push(item);
    groups[orderNum].referenceIds.push(item.id);

    let itemPrice = 0;
    if (item.price) {
      itemPrice = parseFloat(item.price);
    }

    // Soma specs adicionais
    if (item.specs) {
      Object.values(item.specs).forEach((specVal) => {
        if (typeof specVal === "string") {
          const match = specVal.match(/R\$\s*([0-9.,]+)/);
          if (match) {
            const valStr = match[1].replace(/\./g, "").replace(",", ".");
            itemPrice += parseFloat(valStr) || 0;
          }
        }
      });
    }

    groups[orderNum].totalValue += itemPrice;
  });

  // Ajuste do total do pedido legado
  Object.values(groups).forEach((group) => {
    const legacyTotal = group.items[0]?.order?.payment?.total;
    if (legacyTotal && (group.totalValue === 0 || legacyTotal !== "")) {
      if (parseFloat(legacyTotal) > 0) {
        group.totalValue = parseFloat(legacyTotal);
      }
    }
  });

  // Ordena por numero de pedido decrescente
  return Object.values(groups).sort((a, b) => {
    const numA = parseInt(String(a.orderNumber).replace(/\D/g, ""), 10);
    const numB = parseInt(String(b.orderNumber).replace(/\D/g, ""), 10);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numB - numA;
    }
    return b.date - a.date;
  });
}

export default function MonitoramentoSubTab({
  orders, // Não usado como dependência do useEffect para evitar reloads no status sync
  setRawData,
  searchTerm,
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  selectedDetailsOrder,
  setSelectedDetailsOrder,
  syncAllLoading,
  setSyncAllLoading,
  orderLoadings,
  setOrderLoadings,
  copiedTracking,
  setCopiedTracking,
  handleCopyTracking,
  postageLabelsService,
  correiosService,
  pb
}) {
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [dateRangePreset, setDateRangePreset] = useState("30d");
  const [dbOrders, setDbOrders] = useState([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Estados de Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Reseta para a primeira página sempre que alterar os filtros ou termo de busca
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeStatusFilter]);

  // 1. Efeito para carregar todos os pedidos (ativos e arquivados) aplicando filtro de data no BD
  const fetchShippedOrders = async () => {
    setLoadingDb(true);
    setHasSearched(true);
    try {
      let dateFilter = "";
      let searchFilter = "";
      
      const cleanTerm = searchTerm ? searchTerm.trim().replace(/"/g, '\\"') : "";
      const isSearchingSpecific = cleanTerm.length > 0;

      if (isSearchingSpecific) {
        // Se houver termo de busca, ignoramos o filtro de data (busca em todo o histórico)
        const digitsOnly = cleanTerm.replace(/\D/g, "");
        const numberMatch = digitsOnly ? ` || order_number ~ "${digitsOnly}"` : "";
        searchFilter = ` && (order_number ~ "${cleanTerm}" || full_data.order.customer.name ~ "${cleanTerm}" || full_data.customer_name ~ "${cleanTerm}" || full_data.correios_codigo_objeto ~ "${cleanTerm}"${numberMatch})`;
      } else {
        // Se não houver termo de busca, respeitamos o período selecionado
        const now = new Date();
        if (dateRangePreset === "today") {
          const start = new Date(now);
          start.setHours(0, 0, 0, 0);
          dateFilter = ` && created >= "${start.toISOString()}"`;
        } else if (dateRangePreset === "7d") {
          const start = new Date(now);
          start.setDate(start.getDate() - 7);
          start.setHours(0, 0, 0, 0);
          dateFilter = ` && created >= "${start.toISOString()}"`;
        } else if (dateRangePreset === "15d") {
          const start = new Date(now);
          start.setDate(start.getDate() - 15);
          start.setHours(0, 0, 0, 0);
          dateFilter = ` && created >= "${start.toISOString()}"`;
        } else if (dateRangePreset === "30d") {
          const start = new Date(now);
          start.setDate(start.getDate() - 30);
          start.setHours(0, 0, 0, 0);
          dateFilter = ` && created >= "${start.toISOString()}"`;
        } else if (dateRangePreset === "custom") {
          if (filterStartDate) {
            const start = new Date(filterStartDate);
            start.setHours(0, 0, 0, 0);
            dateFilter += ` && created >= "${start.toISOString()}"`;
          }
          if (filterEndDate) {
            const end = new Date(filterEndDate);
            end.setHours(23, 59, 59, 999);
            dateFilter += ` && created <= "${end.toISOString()}"`;
          }
        }
      }

      const records = await pb.collection("production_orders").getFullList({
        filter: `full_data.correios_prepostagem_id != null && full_data.correios_prepostagem_id != ''${dateFilter}${searchFilter}`,
        sort: "-created",
        expand: "fiscal_invoices_via_order"
      });

      const mapped = records.map(r => ({
        id: r.id,
        ...r.full_data,
        ...r,
        expand: r.expand || {},
        exception: r.specs?.exception || r.exception || r.full_data?.exception || ""
      }));

      const grouped = groupOrdersLocal(mapped);
      setDbOrders(grouped);
    } catch (e) {
      console.error("Erro ao buscar envios do PocketBase:", e);
    } finally {
      setLoadingDb(false);
    }
  };

  // Buscamos apenas sob demanda (clique no botão buscar) para melhorar a performance.

  // 2. Aplicar busca por texto e filtros no client-side
  const filtered = useMemo(() => {
    let result = [...dbOrders];

    // Filtro de Busca (Texto)
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(o => {
        const orderNum = String(o.orderNumber || "").toLowerCase();
        const customerName = String(o.customerName || "").toLowerCase();
        return orderNum.includes(term) || customerName.includes(term);
      });
    }

    // Filtro de Status
    if (activeStatusFilter !== "all") {
      result = result.filter(o => {
        const firstItem = o.items?.[0] || {};
        const status = firstItem.full_data?.tracking_status || "AGUARDANDO_POSTAGEM";
        return status === activeStatusFilter;
      });
    }

    return result;
  }, [dbOrders, searchTerm, activeStatusFilter]);

  // 2b. Paginação dos pedidos filtrados
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, currentPage]);

  // 3. Contadores para Badges de Estatística
  const stats = useMemo(() => {
    const counts = {
      all: filtered.length,
      AGUARDANDO_POSTAGEM: 0,
      EM_TRANSITO: 0,
      SAIU_PARA_ENTREGA: 0,
      AGUARDANDO_RETIRADA: 0,
      ENTREGUE: 0,
      EXPIRADO: 0
    };

    filtered.forEach(o => {
      const firstItem = o.items?.[0] || {};
      const status = firstItem.full_data?.tracking_status || "AGUARDANDO_POSTAGEM";
      if (counts[status] !== undefined) {
        counts[status]++;
      } else {
        counts.AGUARDANDO_POSTAGEM++;
      }
    });

    return counts;
  }, [filtered]);

  // 4. Mapeamento de Cores e Rótulos para Exibição
  const getStatusLabel = (status) => {
    switch (status) {
      case "ENTREGUE": return "Entregue";
      case "SAIU_PARA_ENTREGA": return "Saiu p/ Entrega";
      case "AGUARDANDO_RETIRADA": return "Aguardando Retirada";
      case "EM_TRANSITO": return "Em Trânsito";
      case "AGUARDANDO_POSTAGEM": return "Aguardando Postagem";
      case "EXPIRADO": return "Expirado/Cancelado";
      default: return "Aguardando Postagem";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "ENTREGUE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "SAIU_PARA_ENTREGA":
        return "bg-amber-50 text-amber-700 border-amber-300";
      case "AGUARDANDO_RETIRADA":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "EM_TRANSITO":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "EXPIRADO":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-slate-50 text-slate-650 border-slate-200";
    }
  };

  // 5. Sincronizar Rastreio de um Pedido Individual
  const handleSyncTracking = async (order) => {
    const firstItem = order.items?.[0] || {};
    const prepostagemId = firstItem.full_data?.correios_prepostagem_id;
    const tracking = order.trackingCode || firstItem.full_data?.correios_codigo_objeto || order.shipping?.tracking || "";
    
    if (!prepostagemId || !tracking) {
      alert("Este pedido não possui código de rastreamento ou dados suficientes para consulta nos Correios.");
      return;
    }

    setOrderLoadings(prev => ({ ...prev, [order.orderNumber]: "Sincronizando..." }));

    try {
      const result = await correiosService.consultarSro(tracking);
      
      let nextStatus = "AGUARDANDO_POSTAGEM";
      let lastDescription = "Pré-postagem gerada";

      // Desempacota array se o n8n retornar encapsulado
      const cleanResult = Array.isArray(result) ? result[0] : (result || {});

      // 1. Tenta tratar formato SRO (Rastreamento de Objetos)
      if (cleanResult.objetos && cleanResult.objetos.length > 0) {
        const obj = cleanResult.objetos[0];
        // Trata objetos não localizados/inexistentes graciosa e normalmente
        if (obj.mensagem && (obj.mensagem.includes("inexistente") || obj.mensagem.includes("não encontrado") || obj.mensagem.includes("não localizado"))) {
          nextStatus = "AGUARDANDO_POSTAGEM";
          lastDescription = "Objeto ainda não postado";
        } else if (obj.eventos && obj.eventos.length > 0) {
          const event = obj.eventos[0];
          lastDescription = event.descricao || lastDescription;
          const evCode = String(event.codigo || "").toUpperCase();
          const evType = String(event.tipo || "");

          if (evCode === "BDE" || evCode === "BDI" || evCode === "BTE") {
            const isEntregue = evType === "01" || 
                              (String(event.descricao).toLowerCase().includes("entregue") && 
                               !String(event.descricao).toLowerCase().includes("não entregue"));
            
            if (isEntregue) {
              nextStatus = "ENTREGUE";
            } else {
              nextStatus = "EM_TRANSITO";
            }
          } else if (evCode === "LDI") {
            nextStatus = "AGUARDANDO_RETIRADA";
          } else if (evCode === "OEC") {
            nextStatus = "SAIU_PARA_ENTREGA";
          } else if (evCode === "FC") {
            nextStatus = "AGUARDANDO_POSTAGEM";
          } else {
            nextStatus = "EM_TRANSITO";
          }
        } else {
          nextStatus = "AGUARDANDO_POSTAGEM";
          lastDescription = "Objeto ainda não postado";
        }
      }
      // 2. Fallback para formato anterior da Pré-Postagem (CWS)
      else if (cleanResult.itens && cleanResult.itens.length > 0) {
        const item = cleanResult.itens[0];
        const desc = String(item.descStatusAtual || "").toLowerCase();
        lastDescription = item.descStatusAtual || lastDescription;

        if (desc.includes("entregue") && !desc.includes("não entregue")) {
          nextStatus = "ENTREGUE";
        } else if (desc.includes("saiu para entrega") || desc.includes("saiu p/ entrega")) {
          nextStatus = "SAIU_PARA_ENTREGA";
        } else if (desc.includes("retirada") || desc.includes("agência") || desc.includes("agencia")) {
          nextStatus = "AGUARDANDO_RETIRADA";
        } else if (desc.includes("postado") || desc.includes("encaminhado") || desc.includes("trânsito") || desc.includes("preatendido")) {
          nextStatus = "EM_TRANSITO";
        } else if (desc.includes("cancelado") || desc.includes("expirado")) {
          nextStatus = "EXPIRADO";
        }
      }

      // Atualiza no PocketBase
      for (const item of order.items) {
        const updatedFullData = {
          ...(item.full_data || {}),
          tracking_status: nextStatus,
          tracking_last_description: lastDescription,
          tracking_updated_at: new Date().toISOString()
        };

        await pb.collection("production_orders").update(item.id, {
          full_data: updatedFullData
        });
      }

      // Atualiza o estado React local para renderizacao imediata (evita recarregar tudo do BD)
      setDbOrders(prev => prev.map(o => {
        if (o.orderNumber === order.orderNumber) {
          const updatedItems = o.items.map(item => ({
            ...item,
            full_data: {
              ...(item.full_data || {}),
              tracking_status: nextStatus,
              tracking_last_description: lastDescription,
              tracking_updated_at: new Date().toISOString()
            }
          }));
          return { ...o, items: updatedItems };
        }
        return o;
      }));

      // Atualiza o rawData do contexto
      setRawData(prev => prev.map(item => {
        if (order.items.some(i => i.id === item.id)) {
          return { 
            ...item, 
            full_data: {
              ...(item.full_data || {}),
              tracking_status: nextStatus,
              tracking_last_description: lastDescription,
              tracking_updated_at: new Date().toISOString()
            }
          };
        }
        return item;
      }));

    } catch (err) {
      console.error("Erro ao sincronizar rastreamento:", err);
      alert(`Falha ao sincronizar: ${err.message}`);
    } finally {
      setOrderLoadings(prev => {
        const copy = { ...prev };
        delete copy[order.orderNumber];
        return copy;
      });
    }
  };

  // 6. Sincronizar todos os pendentes na página ativa (max 30 registros)
  const handleSyncAllPendents = async () => {
    // Filtramos apenas as pendências da PÁGINA ATUAL para respeitar limites da API dos Correios e do n8n
    const pendents = paginatedOrders.filter(o => {
      const firstItem = o.items?.[0] || {};
      const status = firstItem.full_data?.tracking_status || "AGUARDANDO_POSTAGEM";
      const prepostagemId = firstItem.full_data?.correios_prepostagem_id;
      const tracking = o.trackingCode || firstItem.full_data?.correios_codigo_objeto || o.shipping?.tracking;
      return status !== "ENTREGUE" && status !== "EXPIRADO" && !!prepostagemId && !!tracking;
    });

    if (pendents.length === 0) {
      alert("Nenhum envio pendente de entrega encontrado para atualizar nesta página.");
      return;
    }

    const conf = window.confirm(`Deseja sincronizar ${pendents.length} encomendas pendentes da página atual? Essa ação é executada em etapas com um intervalo seguro para evitar sobrecargas.`);
    if (!conf) return;

    setSyncAllLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const order of pendents) {
      try {
        const firstItem = order.items?.[0] || {};
        const tracking = order.trackingCode || firstItem.full_data?.correios_codigo_objeto || order.shipping?.tracking || "";
        const result = await correiosService.consultarSro(tracking);
        
        let nextStatus = "AGUARDANDO_POSTAGEM";
        let lastDescription = "Pré-postagem gerada";

        // Desempacota array se o n8n retornar encapsulado
        const cleanResult = Array.isArray(result) ? result[0] : (result || {});

        // 1. Tenta tratar formato SRO (Rastreamento de Objetos)
        if (cleanResult.objetos && cleanResult.objetos.length > 0) {
          const obj = cleanResult.objetos[0];
          // Trata objetos não localizados/inexistentes graciosa e normalmente
          if (obj.mensagem && (obj.mensagem.includes("inexistente") || obj.mensagem.includes("não encontrado") || obj.mensagem.includes("não localizado"))) {
            nextStatus = "AGUARDANDO_POSTAGEM";
            lastDescription = "Objeto ainda não postado";
          } else if (obj.eventos && obj.eventos.length > 0) {
            const event = obj.eventos[0];
            lastDescription = event.descricao || lastDescription;
            const evCode = String(event.codigo || "").toUpperCase();
            const evType = String(event.tipo || "");

            if (evCode === "BDE" || evCode === "BDI" || evCode === "BTE") {
              const isEntregue = evType === "01" || 
                                (String(event.descricao).toLowerCase().includes("entregue") && 
                                 !String(event.descricao).toLowerCase().includes("não entregue"));

              if (isEntregue) {
                nextStatus = "ENTREGUE";
              } else {
                nextStatus = "EM_TRANSITO";
              }
            } else if (evCode === "LDI") {
              nextStatus = "AGUARDANDO_RETIRADA";
            } else if (evCode === "OEC") {
              nextStatus = "SAIU_PARA_ENTREGA";
            } else if (evCode === "FC") {
              nextStatus = "AGUARDANDO_POSTAGEM";
            } else {
              nextStatus = "EM_TRANSITO";
            }
          } else {
            nextStatus = "AGUARDANDO_POSTAGEM";
            lastDescription = "Objeto ainda não postado";
          }
        }
        // 2. Fallback para formato anterior da Pré-Postagem (CWS)
        else if (cleanResult.itens && cleanResult.itens.length > 0) {
          const item = cleanResult.itens[0];
          const desc = String(item.descStatusAtual || "").toLowerCase();
          lastDescription = item.descStatusAtual || lastDescription;

          if (desc.includes("entregue") && !desc.includes("não entregue")) {
            nextStatus = "ENTREGUE";
          } else if (desc.includes("saiu para entrega") || desc.includes("saiu p/ entrega")) {
            nextStatus = "SAIU_PARA_ENTREGA";
          } else if (desc.includes("retirada") || desc.includes("agência") || desc.includes("agencia")) {
            nextStatus = "AGUARDANDO_RETIRADA";
          } else if (desc.includes("postado") || desc.includes("encaminhado") || desc.includes("trânsito") || desc.includes("preatendido")) {
            nextStatus = "EM_TRANSITO";
          } else if (desc.includes("cancelado") || desc.includes("expirado")) {
            nextStatus = "EXPIRADO";
          }
        }

        // Atualiza todos os itens do pedido no PocketBase
        for (const item of order.items) {
          const updatedFullData = {
            ...(item.full_data || {}),
            tracking_status: nextStatus,
            tracking_last_description: lastDescription,
            tracking_updated_at: new Date().toISOString()
          };

          await pb.collection("production_orders").update(item.id, {
            full_data: updatedFullData
          });
        }

        // Atualiza o estado local de dbOrders
        setDbOrders(prev => prev.map(o => {
          if (o.orderNumber === order.orderNumber) {
            const updatedItems = o.items.map(item => ({
              ...item,
              full_data: {
                ...(item.full_data || {}),
                tracking_status: nextStatus,
                tracking_last_description: lastDescription,
                tracking_updated_at: new Date().toISOString()
              }
            }));
            return { ...o, items: updatedItems };
          }
          return o;
        }));

        // Atualiza o rawData do contexto
        setRawData(prev => prev.map(item => {
          if (order.items.some(i => i.id === item.id)) {
            return { 
              ...item, 
              full_data: {
                ...(item.full_data || {}),
                tracking_status: nextStatus,
                tracking_last_description: lastDescription,
                tracking_updated_at: new Date().toISOString()
              }
            };
          }
          return item;
        }));

        successCount++;
        // Delay seguro de 500ms (2 requisições por segundo), respeitando a API e o n8n
        await new Promise(r => setTimeout(r, 500));
      } catch (err) {
        console.warn(`Falha na atualização automática do pedido #${order.orderNumber}:`, err);
        failCount++;
      }
    }

    setSyncAllLoading(false);
    alert(`Atualização em lote concluída!\n\nAtualizados com sucesso: ${successCount}\nFalhas: ${failCount}`);
  };

  // 7. Visualizar etiqueta do cache do PocketBase
  const handleViewEtiqueta = async (order) => {
    try {
      setOrderLoadings(prev => ({ ...prev, [order.orderNumber]: "Carregando..." }));
      const shipment = await postageLabelsService.getShipment(order.orderNumber);
      if (shipment && shipment.etiqueta_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.etiqueta_pdf, { token: pb.authStore.token });
        window.open(fileUrl, "_blank");
      } else {
        alert("Nenhum arquivo de etiqueta PDF foi encontrado no cache local.");
      }
    } catch (e) {
      console.error(e);
      alert("Erro ao abrir a etiqueta em cache: " + e.message);
    } finally {
      setOrderLoadings(prev => {
        const copy = { ...prev };
        delete copy[order.orderNumber];
        return copy;
      });
    }
  };

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;

  return (
    <>
      {/* 1. Header do Monitoramento / Filtros e Ações em Lote */}
      <section className="p-4 bg-slate-100 flex flex-col gap-4 border-b border-slate-200 shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          
          {/* Inputs de Filtro de Data */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-2.5 rounded-xl border border-slate-250 shadow-sm flex-1 max-w-4xl">
            <div className="flex items-center gap-1.5 text-slate-500 font-extrabold text-[10px] uppercase tracking-wider pl-1 shrink-0">
              <CalendarRange size={14} className="text-slate-400" />
              <span>Etiqueta Gerada em:</span>
            </div>

            {/* Presets de Data (Pills) */}
            <div className="flex border border-slate-200 rounded-lg overflow-hidden shrink-0">
              {[
                { id: "today", label: "Hoje" },
                { id: "7d", label: "7d" },
                { id: "15d", label: "15d" },
                { id: "30d", label: "30d" },
                { id: "custom", label: "Personalizado" },
                { id: "all", label: "Todos" }
              ].map(preset => (
                <button
                  key={preset.id}
                  onClick={() => setDateRangePreset(preset.id)}
                  className={`px-3 py-1.5 text-xs font-extrabold border-r last:border-r-0 border-slate-200 transition-colors cursor-pointer ${
                    dateRangePreset === preset.id
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-650 hover:bg-slate-100"
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Inputs personalizados condicionados */}
            {dateRangePreset === "custom" && (
              <div className="flex items-center gap-2 animate-fade-in shrink-0">
                <input
                  type="date"
                  className="p-1 text-xs border border-slate-200 rounded outline-none focus:border-blue-500 bg-slate-50 font-bold"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
                <span className="text-slate-400 text-xs font-bold">até</span>
                <input
                  type="date"
                  className="p-1 text-xs border border-slate-200 rounded outline-none focus:border-blue-500 bg-slate-50 font-bold"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
            )}

            <button
              onClick={fetchShippedOrders}
              disabled={loadingDb}
              className="ml-auto px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white font-extrabold text-xs rounded-xl shadow transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95 shrink-0"
              title="Clique para pesquisar os envios no banco de dados baseado nos filtros acima"
            >
              <RefreshCw size={13} className={loadingDb ? "animate-spin" : ""} />
              <span>{loadingDb ? "Buscando..." : "Buscar"}</span>
            </button>
          </div>

          {/* Ações em lote */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={handleSyncAllPendents}
              disabled={syncAllLoading || loadingDb}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-extrabold text-xs px-5 py-3 rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
              title="Atualiza apenas os pacotes pendentes que estão visíveis nesta página (máx. 30)"
            >
              <RefreshCw size={14} className={syncAllLoading ? "animate-spin" : ""} />
              {syncAllLoading ? "Sincronizando..." : "Sincronizar Pendentes da Página"}
            </button>
          </div>
        </div>

        {/* 2. Seletores de Filtros de Status (Visual Cards) */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          {/* Card: Todos */}
          <button
            onClick={() => setActiveStatusFilter("all")}
            className={`px-3 py-2 rounded-xl border text-left shrink-0 transition-all flex items-center gap-2.5 font-bold text-xs shadow-xs cursor-pointer ${
              activeStatusFilter === "all"
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-655 border-slate-200 hover:bg-slate-50"
            }`}
          >
            <span>Todos</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${
              activeStatusFilter === "all" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
            }`}>
              {filtered.length}
            </span>
          </button>

          {/* Outros status */}
          {[
            { id: "AGUARDANDO_POSTAGEM", label: "Aguardando Postagem", color: "bg-slate-200 text-slate-700" },
            { id: "EM_TRANSITO", label: "Em Trânsito", color: "bg-blue-500 text-white" },
            { id: "SAIU_PARA_ENTREGA", label: "Saiu p/ Entrega", color: "bg-amber-500 text-white" },
            { id: "AGUARDANDO_RETIRADA", label: "Aguardando Retirada", color: "bg-purple-600 text-white" },
            { id: "ENTREGUE", label: "Entregue", color: "bg-emerald-600 text-white" },
            { id: "EXPIRADO", label: "Expirados", color: "bg-rose-600 text-white" }
          ].map(f => {
            const count = stats[f.id] || 0;
            return (
              <button
                key={f.id}
                onClick={() => setActiveStatusFilter(f.id)}
                className={`px-3 py-2 rounded-xl border text-left shrink-0 transition-all flex items-center gap-2.5 font-bold text-xs shadow-xs cursor-pointer ${
                  activeStatusFilter === f.id
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-655 border-slate-200 hover:bg-slate-50"
                }`}
              >
                <span>{f.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${
                  activeStatusFilter === f.id ? "bg-white/20 text-white" : f.color
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* 3. Listagem principal do Monitoramento */}
      <div className="flex-1 p-4 pt-0 overflow-hidden flex flex-col bg-slate-100">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {loadingDb ? (
              <div className="h-full flex flex-col justify-center items-center py-16 text-slate-400 gap-3 bg-slate-50/50">
                <RefreshCw className="animate-spin text-blue-500" size={32} />
                <span className="text-sm font-bold">Carregando histórico filtrado de envios...</span>
              </div>
            ) : !hasSearched ? (
              <div className="h-full flex flex-col justify-center items-center py-16 text-slate-450 bg-slate-50/50 gap-2">
                <Truck className="text-slate-350 mb-1" size={44} />
                <span className="text-sm font-extrabold text-slate-700">Filtre e Busque Envios</span>
                <span className="text-xs text-slate-455 max-w-sm text-center leading-relaxed">
                  Selecione o período acima (e/ou preencha a busca geral no topo) e clique no botão <strong>Buscar</strong> para carregar o histórico de envios.
                </span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="h-full flex flex-col justify-center items-center py-16 text-slate-400 bg-slate-50/50">
                <Truck className="text-slate-300 mb-2 animate-bounce" size={40} />
                <span className="text-sm font-bold">Nenhum envio postado localizado nos filtros ativos.</span>
              </div>
            ) : (
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] text-slate-400 font-bold uppercase tracking-wider bg-slate-50/70 shrink-0 sticky top-0 backdrop-blur-md z-10">
                    <th className="py-3 px-4 w-24 pl-6">Pedido</th>
                    <th className="py-3 px-4 w-52">Destinatário</th>
                    <th className="py-3 px-4 w-48">Destino</th>
                    <th className="py-3 px-4 w-28">Valor</th>
                    <th className="py-3 px-4 w-28">Serviço</th>
                    <th className="py-3 px-4 w-44 text-center">Código de Rastreio</th>
                    <th className="py-3 px-4 w-36 text-center">Dt. Etiqueta</th>
                    <th className="py-3 px-4 w-48 text-center">Último Evento / Status</th>
                    <th className="py-3 px-4 text-right pr-6 w-32">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {paginatedOrders.map((order) => {
                    const firstItem = order.items?.[0] || {};
                    const status = firstItem.full_data?.tracking_status || "AGUARDANDO_POSTAGEM";
                    const tracking = order.trackingCode || firstItem.full_data?.correios_codigo_objeto || order.shipping?.tracking || "";
                    const descAtual = firstItem.full_data?.tracking_last_description || "Aguardando primeira postagem nos Correios";
                    const dataGeracao = firstItem.full_data?.correios_etiqueta_gerada_em || order.date;
                    const prepostagemId = firstItem.full_data?.correios_prepostagem_id;

                    const shippingMethodUpper = String(order.shippingMethod || "").toUpperCase();
                    const trackingUpper = String(tracking || "").toUpperCase();

                    // Regra de classificação SEDEX vs PAC:
                    // 1. Pelo código do serviço explícito na forma de envio (ex: 'correios_03220', 'correios_03140')
                    let isSedex = shippingMethodUpper.includes("SEDEX") || 
                                  shippingMethodUpper.includes("03220") || 
                                  shippingMethodUpper.includes("03158") || 
                                  shippingMethodUpper.includes("03140");
                    
                    let isPac = shippingMethodUpper.includes("PAC") || 
                                shippingMethodUpper.includes("03298");

                    // 2. Fallback pelo prefixo do rastreamento gerado (AD para SEDEX, AP para PAC)
                    if (!isSedex && !isPac && trackingUpper) {
                      if (trackingUpper.startsWith("AD") || trackingUpper.startsWith("SD") || trackingUpper.startsWith("SE")) {
                        isSedex = true;
                      } else if (trackingUpper.startsWith("AP") || trackingUpper.startsWith("PE")) {
                        isPac = true;
                      }
                    }

                    // Se for SEDEX de fato, senão assume-se PAC (padrão de menor custo)
                    const isReallySedex = isSedex && !isPac;

                    // Extrair cidade e estado
                    const address = firstItem.shipping?.modified_address || firstItem.shipping?.address || order.shipping?.modified_address || order.shipping?.address || firstItem.full_data?.shipping_address || {};
                    const cidade = address.city || "";
                    const uf = (address.statecode || address.state || "").toUpperCase();
                    const destino = cidade && uf ? `${cidade} - ${uf}` : cidade || uf || "Desconhecido";
                    
                    return (
                      <tr key={order.orderNumber} className="hover:bg-slate-50/40 transition-colors">
                        {/* Pedido */}
                        <td className="py-3.5 px-4 font-mono font-extrabold text-blue-600 pl-6">
                          #{order.orderNumber}
                        </td>

                        {/* Nome do Cliente */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-750 line-clamp-1">
                            {order.customerName}
                          </div>
                        </td>

                        {/* Cidade e UF Destino */}
                        <td className="py-3.5 px-4 text-xs font-semibold text-slate-600">
                          <div className="line-clamp-1" title={destino}>
                            {destino}
                          </div>
                        </td>

                        {/* Valor Total */}
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-700">
                          {formatMoney(order.totalValue)}
                        </td>

                        {/* Serviço */}
                        <td className="py-3.5 px-4">
                          {isReallySedex ? (
                            <span className="bg-red-50 text-red-700 border border-red-100 text-[9px] px-2 py-0.5 rounded font-extrabold">SEDEX</span>
                          ) : (
                            <span className="bg-blue-50 text-blue-700 border border-blue-100 text-[9px] px-2 py-0.5 rounded font-extrabold">PAC</span>
                          )}
                        </td>

                        {/* Rastreio */}
                        <td className="py-3.5 px-4 text-center">
                          {tracking ? (
                            <div className="flex items-center justify-center gap-1.5">
                              <a 
                                href={`https://rastreamento.correios.com.br/app/index.php?objeto=${tracking}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-mono text-xs font-extrabold text-slate-800 hover:text-blue-600 transition-colors underline decoration-slate-300 flex items-center gap-0.5"
                                title="Ver no site dos Correios"
                              >
                                {tracking}
                                <ExternalLink size={10} className="text-slate-400" />
                              </a>
                              <button 
                                onClick={() => handleCopyTracking(tracking)}
                                className="text-slate-400 hover:text-slate-700 p-0.5 rounded hover:bg-slate-100 cursor-pointer" 
                                title="Copiar código de rastreamento"
                              >
                                {copiedTracking === tracking ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic">Não disponível</span>
                          )}
                        </td>

                        {/* Data Etiqueta */}
                        <td className="py-3.5 px-4 text-center text-xs font-semibold text-slate-500">
                          {new Date(dataGeracao).toLocaleDateString("pt-BR")}
                        </td>

                        {/* Status / Último Evento */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-0.5 max-w-[200px] mx-auto">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase border tracking-wider ${getStatusBadgeClass(status)}`}>
                              {getStatusLabel(status)}
                            </span>
                            <span className="text-[9px] text-slate-400 line-clamp-1 text-center font-medium" title={descAtual}>
                              {descAtual}
                            </span>
                          </div>
                        </td>

                        {/* Ações */}
                        <td className="py-3.5 px-4 text-right pr-6">
                          <div className="flex items-center justify-end gap-2">
                            {orderLoadings[order.orderNumber] ? (
                              <span className="text-xs text-slate-400 font-bold animate-pulse">{orderLoadings[order.orderNumber]}</span>
                            ) : (
                              <>
                                {/* Ver Dados da Etiqueta */}
                                <button
                                  type="button"
                                  onClick={() => setSelectedDetailsOrder(order)}
                                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-105 border border-slate-205 rounded-lg transition-colors cursor-pointer bg-white"
                                  title="Ver Dados da Etiqueta"
                                >
                                  <Info size={12} />
                                </button>

                                {/* Visualizar Etiqueta (Cache PDF) */}
                                <button
                                  type="button"
                                  onClick={() => handleViewEtiqueta(order)}
                                  className="p-1.5 text-slate-455 hover:text-blue-600 hover:bg-blue-50 border border-slate-205 rounded-lg transition-colors cursor-pointer bg-white"
                                  title="Visualizar Etiqueta PDF"
                                >
                                  <Eye size={12} />
                                </button>

                                {/* Sincronizar Rastreio dos Correios */}
                                {prepostagemId && (
                                  <button
                                    type="button"
                                    onClick={() => handleSyncTracking(order)}
                                    className="p-1.5 text-slate-455 hover:text-indigo-650 hover:bg-indigo-50 border border-slate-205 rounded-lg transition-colors cursor-pointer bg-white"
                                    title="Sincronizar Rastreamento Correios"
                                  >
                                    <RefreshCw size={12} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 3.1 Barra de Paginação */}
          {!loadingDb && filtered.length > itemsPerPage && (
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0 text-xs font-bold text-slate-600">
              <div>
                Exibindo de {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filtered.length)} de {filtered.length} envios
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="px-3.5 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-xs cursor-pointer transition-all active:scale-95"
                >
                  Anterior
                </button>
                <span className="text-slate-500 font-extrabold px-2">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  className="px-3.5 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-xs cursor-pointer transition-all active:scale-95"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 4. MODAL: DETALHES DE DADOS DA ETIQUETA */}
      {selectedDetailsOrder && (
        <LabelDetailsModal
          order={selectedDetailsOrder}
          onClose={() => setSelectedDetailsOrder(null)}
        />
      )}
    </>
  );
}

// Sub-componente Modal para Detalhes dos Dados da Etiqueta
function LabelDetailsModal({ order, onClose }) {
  const firstItem = order.items?.[0] || {};
  const address = firstItem.shipping?.modified_address || firstItem.shipping?.address || order.shipping?.modified_address || order.shipping?.address || firstItem.full_data?.shipping_address || {};
  const customer = firstItem.order?.customer || order.full_data?.order?.customer || {};
  
  // Detalhes fiscais
  const activeInvoice = order.activeInvoice || (order.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  
  // Chaves adicionais do seguro/ar
  const temAR = firstItem.full_data?.correios_ar === true || !!firstItem.full_data?.shipping?.ar;
  const temSeguro = firstItem.full_data?.correios_seguro === true || !!firstItem.full_data?.shipping?.seguro;
  const valorSegurado = firstItem.full_data?.correios_valor_segurado || firstItem.full_data?.shipping?.valorSegurado || 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center z-50 animate-fade-in">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-lg w-full mx-4 animate-scale-in max-h-[85vh] overflow-y-auto custom-scrollbar text-slate-800">
        <div className="flex justify-between items-start border-b border-slate-100 pb-3 mb-4">
          <div>
            <h3 className="font-extrabold text-slate-900 text-base">Metadados da Etiqueta - Pedido #{order.orderNumber}</h3>
            <p className="text-[10px] text-slate-450 mt-0.5">Parâmetros originais enviados para a API dos Correios v2</p>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-655 font-extrabold text-sm p-1 rounded hover:bg-slate-100 cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-col gap-4 text-xs">
          
          {/* Seção 1: Destinatário */}
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">1. Destinatário</span>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 flex flex-col gap-1">
              <div><strong className="text-slate-600">Nome:</strong> <span className="font-bold">{address.recipient || customer.name || order.customerName}</span></div>
              {customer.cpf && <div><strong className="text-slate-600">CPF/CNPJ:</strong> <span className="font-mono">{customer.cpf}</span></div>}
              {customer.phone && <div><strong className="text-slate-600">Celular:</strong> <span className="font-mono">{customer.phone}</span></div>}
              {customer.email && <div><strong className="text-slate-600">E-mail:</strong> <span>{customer.email}</span></div>}
            </div>
          </div>

          {/* Seção 2: Endereço */}
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">2. Endereço de Envio</span>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 flex flex-col gap-1">
              <div><strong className="text-slate-600">Rua:</strong> <span className="font-bold">{address.street || ""}</span>, <strong className="text-slate-600">Nº:</strong> <span className="font-bold">{address.number || "S/N"}</span></div>
              {address.complemento && <div><strong className="text-slate-600">Complemento:</strong> <span>{address.complemento}</span></div>}
              <div><strong className="text-slate-600">Bairro:</strong> <span>{address.district || ""}</span></div>
              <div><strong className="text-slate-600">Cidade/UF:</strong> <span className="font-bold">{address.city || ""} / {(address.statecode || address.state || "").toUpperCase()}</span></div>
              <div><strong className="text-slate-600">CEP:</strong> <span className="font-mono font-bold">{address.zip || ""}</span></div>
            </div>
          </div>

          {/* Seção 3: Dimensões e Embalagem */}
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">3. Pacote e Dimensões</span>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 grid grid-cols-2 gap-2">
              <div><strong className="text-slate-600">Formato:</strong> <span>Caixa / Pacote</span></div>
              <div><strong className="text-slate-600">Peso Estimado:</strong> <span className="font-bold">120g (0.12 kg)</span></div>
              <div><strong className="text-slate-600">Dimensões:</strong> <span className="font-mono">16 x 11 x 6 cm</span></div>
              <div><strong className="text-slate-600">Conteúdo:</strong> <span className="italic line-clamp-1">{order.items?.map(i => i.sku).join(", ") || "Joias em Prata"}</span></div>
            </div>
          </div>

          {/* Seção 4: Serviços Contratados */}
          <div>
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">4. Serviços Adicionais & Fiscais</span>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-150 flex flex-col gap-1.5">
              <div className="flex justify-between items-center">
                <strong className="text-slate-600">Aviso de Recebimento (AR):</strong>
                {temAR ? (
                  <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold border border-emerald-100">Ativado</span>
                ) : (
                  <span className="bg-slate-100 text-slate-550 px-2 py-0.5 rounded text-[10px] font-bold">Inativo</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <strong className="text-slate-600">Seguro Declarado:</strong>
                {temSeguro ? (
                  <div className="flex items-center gap-1.5">
                    <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold border border-emerald-100">Ativado</span>
                    <span className="font-mono font-bold text-slate-700">{formatMoney(valorSegurado)}</span>
                  </div>
                ) : (
                  <span className="bg-slate-100 text-slate-550 px-2 py-0.5 rounded text-[10px] font-bold">Inativo</span>
                )}
              </div>
              {activeInvoice?.nfe_chave && (
                <div className="pt-1.5 border-t border-slate-200 mt-1 flex flex-col gap-0.5">
                  <strong className="text-slate-600">Chave da NF-e vinculada:</strong>
                  <span className="font-mono text-[10px] break-all bg-white p-1.5 rounded border border-slate-150 text-slate-700 font-bold">{activeInvoice.nfe_chave}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="mt-6 pt-3 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs bg-slate-800 hover:bg-slate-900 text-white font-extrabold px-6 py-2.5 rounded-xl shadow-md transition-all uppercase"
          >
            Fechar Detalhes
          </button>
        </div>
      </div>
    </div>
  );
}
